import {
  type ApiRoleStrategy,
  ROLES,
  api,
  stringifyQueryObj,
  useQuery,
  useRoleConf,
} from "@pharmupp/p3-front-commons"
import type { ApiLoyaltyCategoryEnum } from "../../../types"

export const useLoyaltyCategoryApi = ({
  categoryEnum,
  year,
  adherentId,
}: {
  categoryEnum: ApiLoyaltyCategoryEnum
  year: number
  adherentId?: string
}) => {
  const { endpoint, queryKey } = useRoleConf(apiRoleConf(categoryEnum, adherentId))
  const queryParams = stringifyQueryObj({ year })

  const { data: loyaltyCategory, isLoading: loading } = useQuery({
    queryKey: [...queryKey, year],
    queryFn: () => api.get<LoyaltyCategory>(`${endpoint}?${queryParams}`),
    select: (rawLoyaltyCategory) => ({
      ...rawLoyaltyCategory,
      subCategories:
        rawLoyaltyCategory.subCategories?.map((subCategory) => ({
          ...subCategory,
          bonusChallenges:
            subCategory.challenges?.filter((challenge) => challenge.bonus) || [],
          nonBonusChallenges:
            subCategory.challenges?.filter((challenge) => !challenge.bonus) || [],
        })) || [],
    }),
  })

  return {
    loading,
    loyaltyCategory,
  }
}

export interface LoyaltyCategory {
  id: number
  subCategories?: SubCategory[] | null
  challenges?: Challenge[] | null
  history?: ChallengeHistory[] | null
  name: string
  orderNum: number
  totalEarnPoints: number
  totalExpectedPoints: number
  lastYearValue?: number | null
  ratio: number
  totalRealized: number
  unlockedChallenge: number
}

export type SubCategory = Omit<LoyaltyCategory, "subCategories"> & {
  subCategories?: null
  hasBonus: boolean
}

export type SelectedSubCategory = SubCategory & {
  bonusChallenges: Challenge[]
  nonBonusChallenges: Challenge[]
}

export interface Challenge {
  expectedPoint: number
  formattedLabel: string
  quantity?: string
  tag?: string | null
  caption?: string | null
  numberField?: string | null
  unitSuffix?: string | null
  tooltip?: string | null
  orderNumber: number
  unlocked: boolean
  bonus: boolean
  totalPointsUnlocked?: number | null
}

export type ChallengeHistory = {
  adherentId: number
  historyDate: string
  label: string
  pointDiff: number
  pointDiffRelative: number | null
  year: number | null
  historyDateWithoutTime: string
  descriptionItem: {
    id: number
    year: number
    expectedPoint: number
    maxMultiplier: number
    formattedLabel: string
    formattedLabelPlural: string | null
    caption: string
    tag: string | null
    numberField: string
    unitSuffix: string | null
    tooltip: string | null
    orderNumber: number
    minimum: number
    bonus: boolean
  } | null
} & SdavHistory

export interface SdavHistory {
  nbItemsRelative?: number
  nbItemsRelativeGoal?: number
}

const presidentApiRoleConf = (
  categoryEnum: ApiLoyaltyCategoryEnum,
  adherentId: string,
) => ({
  endpoint: `/api/loyalties/upp/adherent/${adherentId}/detailed/${categoryEnum}`,
  queryKey: ["loyalties", "upp", "adherent", adherentId, "detailed", categoryEnum],
})

const adherentApiRoleConf = (categoryEnum: ApiLoyaltyCategoryEnum) => ({
  endpoint: `/api/loyalties/my-adherent/detailed/${categoryEnum}`,
  queryKey: ["loyalties", "my-adherent", "detailed", categoryEnum],
})

const apiRoleConf = (
  categoryEnum: ApiLoyaltyCategoryEnum,
  adherentId?: string,
): ApiRoleStrategy => ({
  [ROLES.ADHERENT_HOLDER]: adherentApiRoleConf(categoryEnum),
  [ROLES.ADHERENT_TEAM]: adherentApiRoleConf(categoryEnum),
  ...(adherentId && {
    [ROLES.UPP_PRESIDENT]: presidentApiRoleConf(categoryEnum, adherentId),
    [ROLES.UPP_REPRESENTATIVE]: presidentApiRoleConf(categoryEnum, adherentId),
    [ROLES.ADMIN]: {
      endpoint: `/api/loyalties/adherent/${adherentId}/detailed/${categoryEnum}`,
      queryKey: ["loyalties", "adherent", adherentId, "detailed", categoryEnum],
    },
    [ROLES.ADHERENT_ADVISOR]: {
      endpoint: `/api/loyalties/my-sector/adherent/${adherentId}/detailed/${categoryEnum}`,
      queryKey: [
        "loyalties",
        "my-sector",
        "adherent",
        adherentId,
        "detailed",
        categoryEnum,
      ],
    },
  }),
})
